// "Get to know you" prompts employees answer for their Sentry People and Sentry Chat profiles.
// Keys are stored with each answer, so keep them stable when wording changes or prompts are retired.
export const PEOPLE_PROFILE_PROMPTS = [
	{ key: 'favouriteSport', question: 'What is your favourite sport?' },
	{ key: 'favouriteWeekendActivity', question: 'What is your favourite thing to do on the weekend?' },
	{ key: 'favouriteHolidayDestination', question: 'What is your favourite holiday destination?' },
	{ key: 'favouriteRecentMovie', question: 'What is your favourite movie you have watched recently?' },
	{ key: 'favouriteRecentTvShow', question: 'What is your favourite tv show you have watched recently?' },
	{ key: 'pets', question: 'What pets do you have?' },
	{ key: 'learnThisYear', question: 'What\'s something new you\'d love to learn this year?' },
	{ key: 'grewUp', question: 'Where did you grow up?' },
	{ key: 'favouriteChocolateBar', question: 'What is your favourite chocolate bar?' },
	{ key: 'vendAnyProduct', question: 'If you could vend any product, what would it be?' },
	{ key: 'favouriteMealToCook', question: 'What is your favourite meal to cook?' },
	{ key: 'overspendOn', question: 'What are you most likely to spend too much money on?' },
	{ key: 'liveAnywhere', question: 'If you could live anywhere in the world, where would it be?' },
	{ key: 'favouriteActivity', question: 'What is your favourite activity?' },
	{ key: 'bingeTvSeries', question: 'What type of TV series do you binge?' },
	{ key: 'favouritePodcastType', question: 'What\'s your favourite type of podcast?' },
	{ key: 'birthdayCelebration', question: 'How do you usually celebrate your birthday?' },
];

export const PEOPLE_PROFILE_PROMPT_MAX_ANSWERS = 3;
export const PEOPLE_PROFILE_PROMPT_ANSWER_MAX_LENGTH = 300;
// Shown in the browser and returned by onboarding acceptance, so both say the same thing.
export const PEOPLE_PROFILE_PHOTO_REQUIRED_MESSAGE = 'Upload a photo of yourself.';
export const PEOPLE_PROFILE_PROMPT_REQUIRED_MESSAGE = 'Pick at least 1 question and answer it.';
export const PEOPLE_PROFILE_ABOUT_YOU_DISCLAIMER = 'Your photo and answers will be shown on your Sentry Chat and Sentry People profiles, so other team members can see them.';

const PROFILE_PROMPT_QUESTIONS_BY_KEY = new Map(PEOPLE_PROFILE_PROMPTS.map(prompt => [prompt.key, prompt.question]));

const toTrimmedString = value => (typeof value === 'string' ? value.trim() : '');

// Stored answers keep the question as it was answered, so they still display after the list changes.
export const getPeopleProfilePromptAnswers = value => (Array.isArray(value) ? value : [])
	.map(item => ({
		key: toTrimmedString(item?.key),
		question: toTrimmedString(item?.question),
		answer: toTrimmedString(item?.answer),
	}))
	.filter(item => item.key && item.question && item.answer);

export const hasPeopleProfileAboutYou = ({ profilePhotoUrl, profilePrompts }) => (
	Boolean(toTrimmedString(profilePhotoUrl)) && getPeopleProfilePromptAnswers(profilePrompts).length > 0
);

// Validates submitted { key, answer } items. The question comes from the current list, or from the
// stored answer when that prompt has since left the list.
export const resolvePeopleProfilePromptAnswers = ({ answers, storedAnswers = [] }) => {
	if (answers !== null && answers !== undefined && !Array.isArray(answers)) {
		return { answers: [], error: 'Answers must be a list.' };
	}

	const items = answers || [];
	if (items.length > PEOPLE_PROFILE_PROMPT_MAX_ANSWERS) {
		return { answers: [], error: `Answer up to ${PEOPLE_PROFILE_PROMPT_MAX_ANSWERS} questions.` };
	}

	const storedQuestionsByKey = new Map(getPeopleProfilePromptAnswers(storedAnswers).map(item => [item.key, item.question]));
	const usedKeys = new Set();
	const resolvedAnswers = [];

	for (const item of items) {
		const key = toTrimmedString(item?.key);
		const question = PROFILE_PROMPT_QUESTIONS_BY_KEY.get(key) || storedQuestionsByKey.get(key) || '';
		const answer = toTrimmedString(item?.answer);

		if (!question) {
			return { answers: [], error: 'Choose a question from the list for each answer.' };
		}
		if (usedKeys.has(key)) {
			return { answers: [], error: 'Choose a different question for each answer.' };
		}
		if (!answer) {
			return { answers: [], error: 'Answer each question you picked.' };
		}
		if (answer.length > PEOPLE_PROFILE_PROMPT_ANSWER_MAX_LENGTH) {
			return { answers: [], error: `Keep each answer to ${PEOPLE_PROFILE_PROMPT_ANSWER_MAX_LENGTH} characters or fewer.` };
		}

		usedKeys.add(key);
		resolvedAnswers.push({ key, question, answer });
	}

	return { answers: resolvedAnswers, error: '' };
};

// Editing forms (Sentry People and the Sentry phone app) keep { key, answer } rows;
// a row with neither a question nor an answer is ignored.
export const createEmptyProfilePromptDraft = () => ({ key: '', answer: '' });

const getFilledProfilePromptDrafts = drafts => (Array.isArray(drafts) ? drafts : [])
	.filter(draft => draft?.key || String(draft?.answer || '').trim());

export const toProfilePromptDrafts = (answers) => {
	const drafts = getPeopleProfilePromptAnswers(answers).map(({ key, answer }) => ({ key, answer }));
	return drafts.length ? drafts : [createEmptyProfilePromptDraft()];
};

export const resolveProfileAboutYouStatus = ({ photoUrl, drafts, storedAnswers }) => {
	const { answers, error } = resolvePeopleProfilePromptAnswers({
		answers: getFilledProfilePromptDrafts(drafts),
		storedAnswers,
	});
	const promptsError = error || (answers.length ? '' : PEOPLE_PROFILE_PROMPT_REQUIRED_MESSAGE);
	const photoError = photoUrl ? '' : PEOPLE_PROFILE_PHOTO_REQUIRED_MESSAGE;

	return {
		answers,
		photoError,
		promptsError,
		isComplete: !photoError && !promptsError,
	};
};

// Question choices for one row: prompts not picked in the other rows, plus a retired prompt
// the person already answered, so it stays selectable for them.
export const getProfilePromptOptions = ({ drafts, index, storedAnswers }) => {
	const usedKeys = new Set(drafts.filter((_, rowIndex) => rowIndex !== index).map(item => item.key));
	const options = PEOPLE_PROFILE_PROMPTS
		.filter(prompt => !usedKeys.has(prompt.key))
		.map(prompt => ({ value: prompt.key, label: prompt.question }));
	const rowKey = drafts[index]?.key;
	const storedQuestion = rowKey
		? getPeopleProfilePromptAnswers(storedAnswers).find(item => item.key === rowKey)?.question
		: '';
	if (storedQuestion && !options.some(option => option.value === rowKey)) {
		options.push({ value: rowKey, label: storedQuestion });
	}

	return options;
};

// Saved answers send only the prompt key and answer; the server records the question text.
export const toProfilePromptInput = answers => answers.map(({ key, answer }) => ({ key, answer }));
